import { useEffect, useMemo, useState } from "react";
import { getDailyReportsInRealTime } from "../../../Config/db/daily-report.retrieve";
import { getConsolidatedActionsInRealTime } from "../../../Config/db/actions.retrieve";

const EstimateMath = {
  m: 1,
  h: 60,
  d: 8 * 60, // 1 day = 8 working hours
};

const EstimateParseConfig = {
  d: 8 * 60, // 1 day = 8 working hours
  h: 60,
  m: 1,
};

const estimateParser = (minutes) => {
  let parsedString = "";
  let totalMinutes = minutes;
  let counter = 0;

  while (EstimateParseConfig.d <= totalMinutes) {
    counter += 1;
    totalMinutes -= EstimateParseConfig.d;
  }
  if (counter) {
    parsedString = `${counter}d`;
    counter = 0;
  }

  while (EstimateParseConfig.h <= totalMinutes) {
    counter += 1;
    totalMinutes -= EstimateParseConfig.h;
  }
  if (counter) {
    parsedString = parsedString ? `${parsedString} ${counter}h` : `${counter}h`;
    counter = 0;
  }

  if (totalMinutes) {
    parsedString = parsedString
      ? `${parsedString} ${totalMinutes}m`
      : `${totalMinutes}m`;
  }
  return parsedString;
};

/**
 * Returns true if the estimate string is effectively zero (e.g. "0", "0m", "0h", "0d 0h").
 */
const isZeroEstimate = (est) => {
  if (!est || !est.trim()) return true;
  // Strip all units, spaces — if only zeros remain, it's zero
  return est.replace(/[a-zA-Z\s]/g, "").replace(/0/g, "") === "";
};

const sumUpEstimate = (actions) => {
  const hasAnyEstimate = actions.some((a) => !isZeroEstimate(a.estimate));
  if (!hasAnyEstimate) return "";

  let splittedEstimates = [];
  actions.forEach((each) => {
    if (!isZeroEstimate(each.estimate)) {
      const splittedEstimate = each.estimate.split(" ");
      splittedEstimates = [...splittedEstimate, ...splittedEstimates];
    }
  });

  const totalMinutes = splittedEstimates.reduce(
    (accumulateMinutes, rawEstimate) => {
      const suffix = rawEstimate.replace(/^[0-9]+/g, "");
      const match = rawEstimate.match(/^[0-9]+/g);
      if (!match) return accumulateMinutes;
      const time = parseInt(match[0], 10);
      return accumulateMinutes + time * (EstimateMath[suffix] || 0);
    },
    0
  );

  return estimateParser(totalMinutes);
};

export default function useConsolidatedActions(projectInfo) {
  const [reports, setReports] = useState([]);
  const [actions, setActions] = useState([]);
  const [estimatedToResolve, setEstimatedToResolve] = useState("");

  useEffect(() => {
    if (projectInfo?.id) {
      const unSub = getDailyReportsInRealTime(projectInfo.id, (reports) => {
        setReports(reports);
      });
      return () => unSub?.();
    } else {
      setReports([]);
    }
  }, [projectInfo?.id]);

  // Stabilise dependency: only re-subscribe when the actual set of report IDs
  // changes, NOT on every Firestore snapshot (which creates a new array ref).
  const reportIdKey = useMemo(
    () => reports.map((r) => r.id).sort().join(","),
    [reports]
  );

  useEffect(() => {
    if (!reportIdKey) {
      setActions([]);
      setEstimatedToResolve("");
      return;
    }
    const ids = reportIdKey.split(",");
    const unSub = getConsolidatedActionsInRealTime(ids, (actions) => {
      setActions(actions);
    });
    return () => unSub?.();
  }, [reportIdKey]);

  useEffect(() => {
    if (actions.length) {
      const total = sumUpEstimate(actions);
      setEstimatedToResolve(total || "Unestimated");
    } else {
      setEstimatedToResolve("");
    }
  }, [actions]);

  return { actions, reports, estimatedToResolve };
}

export { isZeroEstimate };
